import type { AffiliateLinks, SelectedProduct } from '@nfa/types';

export const AMAZON_AFFILIATE_TAG = 'notfa-21';

const ASIN_PATTERN = /\/(?:dp|gp\/product)\/([A-Z0-9]{10})(?:[/?#]|$)/i;

/** Extract an ASIN from affiliate links. Prefers explicit amazon_asin, falls back to URL extraction. Returns null for search-only URLs. */
export function extractAsin(affiliateLinks: AffiliateLinks | undefined): string | null {
  if (!affiliateLinks) return null;

  // Prefer explicitly stored ASIN
  if (affiliateLinks.amazon_asin) {
    return affiliateLinks.amazon_asin.toUpperCase();
  }

  // Fall back to extracting from URL
  const url = affiliateLinks.amazon;
  if (!url) return null;

  const match = url.match(ASIN_PATTERN);
  return match ? match[1].toUpperCase() : null;
}

/** Extract an ASIN from a URL string directly. Returns null for search URLs, non-Amazon URLs, or invalid formats. */
export function extractAsinFromUrl(url: string): string | null {
  if (!url) return null;

  const match = url.match(ASIN_PATTERN);
  if (!match) return null;

  return match[1].toUpperCase();
}

/** Build an Amazon multi-item cart URL from selected products. Returns null if no valid ASINs found. */
export function buildAmazonCartUrl(
  items: SelectedProduct[],
  affiliateTag: string = AMAZON_AFFILIATE_TAG
): string | null {
  const asins = new Set<string>();

  for (const item of items) {
    const asin = extractAsin(item.affiliate_links);
    if (asin) asins.add(asin);
  }

  if (asins.size === 0) return null;

  const params = new URLSearchParams();
  params.set('AssociateTag', affiliateTag);

  let index = 1;
  for (const asin of asins) {
    params.set(`ASIN.${index}`, asin);
    params.set(`Quantity.${index}`, '1');
    index++;
  }

  return `https://www.amazon.co.uk/gp/aws/cart/add.html?${params.toString()}`;
}

/** Count the number of unique items with extractable ASINs. */
export function countAmazonCartItems(items: SelectedProduct[]): number {
  const asins = new Set<string>();

  for (const item of items) {
    const asin = extractAsin(item.affiliate_links);
    if (asin) asins.add(asin);
  }

  return asins.size;
}

const VALID_ASIN_PATTERN = /^[A-Z0-9]{10}$/i;
const AMAZON_DOMAIN_PATTERN = /^https?:\/\/(www\.)?amazon\.(co\.uk|com|de|fr|es|it|ca|co\.jp|com\.au)/i;

/**
 * Build an Amazon product page URL from an ASIN.
 * Returns null if the ASIN is invalid.
 *
 * @param asin - The 10-character Amazon ASIN
 * @param affiliateTag - Optional affiliate tag (defaults to notfa-21)
 * @returns Full Amazon product URL with affiliate tag, or null if invalid
 */
export function buildAmazonProductUrl(
  asin: string,
  affiliateTag: string = AMAZON_AFFILIATE_TAG
): string | null {
  if (!asin || !VALID_ASIN_PATTERN.test(asin)) {
    return null;
  }

  const normalizedAsin = asin.toUpperCase();
  return `https://www.amazon.co.uk/dp/${normalizedAsin}?tag=${affiliateTag}`;
}

/**
 * Ensure an Amazon URL has the correct affiliate tag.
 * Non-Amazon URLs are returned unchanged.
 *
 * @param url - The URL to process
 * @param affiliateTag - Optional affiliate tag (defaults to notfa-21)
 * @returns URL with affiliate tag added or replaced
 */
export function ensureAffiliateTag(
  url: string,
  affiliateTag: string = AMAZON_AFFILIATE_TAG
): string {
  if (!url) return '';

  // Check if it's an Amazon URL
  if (!AMAZON_DOMAIN_PATTERN.test(url)) {
    return url;
  }

  try {
    const urlObj = new URL(url);

    // Remove existing tag if present and add our tag
    urlObj.searchParams.delete('tag');
    urlObj.searchParams.set('tag', affiliateTag);

    return urlObj.toString();
  } catch {
    // If URL parsing fails, return original
    return url;
  }
}

/**
 * Get a properly tagged Amazon product URL from affiliate links.
 * Prefers explicit amazon_asin to build a clean URL.
 * Falls back to adding tag to existing amazon URL.
 * Returns null for search-only URLs without explicit ASIN.
 *
 * @param affiliateLinks - The affiliate links object
 * @param affiliateTag - Optional affiliate tag (defaults to notfa-21)
 * @returns Amazon product URL with affiliate tag, or null if not available
 */
export function getAmazonProductUrl(
  affiliateLinks: AffiliateLinks | undefined,
  affiliateTag: string = AMAZON_AFFILIATE_TAG
): string | null {
  if (!affiliateLinks) return null;

  // If explicit ASIN is provided, build a clean URL from it
  if (affiliateLinks.amazon_asin) {
    return buildAmazonProductUrl(affiliateLinks.amazon_asin, affiliateTag);
  }

  // If no amazon URL, return null
  const amazonUrl = affiliateLinks.amazon;
  if (!amazonUrl) return null;

  // Check if it's a search URL (no ASIN extractable)
  const asin = extractAsinFromUrl(amazonUrl);
  if (!asin) {
    // It's a search URL or invalid format - can't create product link
    return null;
  }

  // Has valid product URL - ensure it has our affiliate tag
  return ensureAffiliateTag(amazonUrl, affiliateTag);
}
